import ko from "knockout";
import _ from "lodash";
import qualityManagementService from "services/qualityManagementService";
import errorCallbackFactory from "util/errorCallbackFactory";
import AudioPlayerView from "viewModels/widgets/audio/AudioPlayerView";
import PlaybackHeaderView from "viewModels/PlaybackHeaderView";
import ChatDetailsView from "viewModels/ChatDetailsView";
import toasts from "services/toastService";
import ScreenRecordingDetailsView from "viewModels/ScreenRecordingDetailsView";
import RecordingRestoreDetailsView from "viewModels/RecordingRestoreDetailsView";
import FileStateEnum from "viewModels/util/FileStateEnum";
import ViewModel from "viewModels/ViewModel";
import { maxDescriptionChar } from "models/Annotation";
import Bluebird from "bluebird";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";

const RecordingView = ViewModel.extend(function (
    conversationId,
    recordingModel,
    options,
    conversationObservable,
    index = 0,
    recordingRequestError = false,
    errorStatus = -1
) {
    const self = this;
    const isSparkFlareFTEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_1418_IDUI_SPARK_V4_FLARE
    );

    const audioOptions = _.assign(
        { autoplay: false },
        _.omit(options, "displayed")
    );
    self.displayed = ko.observable(options ? !!options.displayed : false);
    self.conversation = ko.isObservable(conversationObservable)
        ? conversationObservable
        : ko.observable(conversationObservable);
    self.conversationId = ko.observable(conversationId);
    self.recording = ko.observable();
    self.chatDetailsView = ko.observable();
    self.screenRecordingDetailsView = ko.observable();
    const isAudioElementRendered = ko.observable(false);
    self.audioRendered = function () {
        isAudioElementRendered(true);
    };

    self.hasAudio = ko.observable(false);
    self.hasChat = ko.observable(false);
    self.hasEmail = ko.observable(false);
    self.hasMessages = false; // Does not need to be observable. Will always be false
    self.hasScreenRecording = ko.observable(false);

    self.isDeleted = recordingModel.fileState === FileStateEnum.DELETED;
    self.isArchived = recordingModel.fileState === FileStateEnum.ARCHIVED;
    self.recordingDeletedTitle = "playback.recordingIsDeleted.title";
    self.recordingDeletedDescription =
        "playback.recordingIsDeleted.description";

    self.audioPlayerView = self.subView(
        AudioPlayerView,
        null,
        self.conversationId(),
        audioOptions,
        recordingModel.id
    );

    self.header = ko.computed(function () {
        if (self.screenRecordingDetailsView()) {
            return self.screenRecordingDetailsView().header;
        } else if (
            self.chatDetailsView() &&
            (self.isRecordingAvailable() || self.isRecordingRestoring())
        ) {
            return self.chatDetailsView().chatHeader;
        } else if (self.isDeleted || self.isArchived) {
            return self.subView(PlaybackHeaderView, "", recordingModel.media);
        } else if (self.audioPlayerView.header && recordingModel.id) {
            return self.audioPlayerView.header;
        } else {
            return false;
        }
    });

    self.recordingRestoreDetails = self.subView(
        RecordingRestoreDetailsView,
        self.recording,
        {
            afterRender: self.audioRendered,
            header: self.header,
        }
    );

    self.isRecordingLoading = ko.observable(true);
    self.isRecordingAvailable = ko.observable(false);
    self.isRecordingUploading = ko.observable(false);
    self.showLongRunningMessage = ko.observable(false);
    self.recordingRequestError = ko.observable(recordingRequestError);
    self.httpErrorStatus = ko.observable(errorStatus);
    self.isRecordingRestoring = ko.observable(false);

    self.isAvailableAudioRecordingWithMissingMedia = self.computed(function () {
        const recording = self.recording();
        if (recording) {
            const media = ko.unwrap(recordingModel.media);
            return (
                media === "audio" &&
                self.isRecordingAvailable() &&
                !self.hasAudio()
            );
        }
        return false;
    });

    _.assign(self, audioOptions);

    //Adjust annotations for display
    self.adjustAnnotations = function (annotations) {
        _.each(annotations, function (annotation) {
            //Adjust secure pause duration and description
            if (annotation.type === "Pause") {
                // Updated Players should display a secure pause of 1.5 seconds, and have no description
                annotation.recordingDurationMs = 1500;
                annotation.description = "";
            }
        });
    };

    const loadRecordingResponse = function (recordingModel) {
        if (recordingModel.annotations) {
            self.adjustAnnotations(recordingModel.annotations);
        }

        self.showLongRunningMessage(false);
        self.isRecordingLoading(false);
        self.recording(recordingModel);
    };

    const setRecording = function (recordingModel) {
        self.conversationId(recordingModel.conversationId);
        loadRecordingResponse(recordingModel);
    };

    setRecording(recordingModel);

    self.hasAnnotationText = self.computed(function () {
        return self.audioPlayerView.annotationTextInput().length > 0;
    });

    self.computed(function () {
        if (self.recording()) {
            const fileState = ko.unwrap(self.recording().fileState);
            if (fileState) {
                self.isRecordingAvailable(
                    fileState.toLowerCase() ===
                        FileStateEnum.AVAILABLE.toLowerCase() ||
                        fileState.toLowerCase() ===
                            FileStateEnum.RESTORED.toLowerCase()
                );
                self.isRecordingUploading(
                    fileState.toLowerCase() ===
                        FileStateEnum.UPLOADING.toLowerCase()
                );
                self.isRecordingRestoring(
                    fileState.toLowerCase() ===
                        FileStateEnum.RESTORING.toLowerCase()
                );
            }

            self.hasAudio(
                ko.unwrap(self.recording().media) === "audio" &&
                    (self.recording().mediaUri ||
                        (self.recording().mediaUris &&
                            !_.isEmpty(self.recording().mediaUris)))
            );
            if (isAudioElementRendered()) {
                if (self.recording().mediaUris) {
                    const channels = [];
                    _.each(self.recording().mediaUris, function (mediaInfo) {
                        const unwrapped = ko.toJS(mediaInfo);
                        channels.push({
                            audioUri: unwrapped.mediaUri,
                            waveformUri: unwrapped.waveUri,
                            waveformData: unwrapped.waveformData,
                        });
                    });

                    if (!_.isEmpty(self.audioPlayerView.audioChannels())) {
                        self.disposeSubView(self.audioPlayerView);
                        self.audioPlayerView = self.subView(
                            AudioPlayerView,
                            null,
                            ko.unwrap(self.conversationId),
                            audioOptions,
                            recordingModel.id
                        );
                    }
                    self.audioPlayerView.setChannels(channels);
                } else if (self.recording().mediaUri) {
                    const unwrapped = ko.toJS(self.recording);
                    self.audioPlayerView.setChannels([
                        {
                            audioUri: unwrapped.mediaUri,
                            waveformUri: unwrapped.waveUri,
                        },
                    ]);
                }
                if (self.recording().annotations) {
                    self.audioPlayerView.annotationsList(
                        self.recording().annotations
                    );
                }
                // we fetch the duration of the voice recording from the audio file
                // instead of based on the start/end time pair
                self.audioPlayerView.header.setStartEndTime(
                    self.recording().startTime,
                    self.recording().endTime
                );
            }

            self.hasEmail(ko.unwrap(self.recording().media) === "email");

            self.hasChat(
                self.recording().transcript ||
                    ko.unwrap(self.recording().media) === "chat"
            );
            if (
                self.hasChat() &&
                (self.isRecordingAvailable() || self.isRecordingRestoring())
            ) {
                self.chatDetailsView(
                    self.subView(
                        ChatDetailsView,
                        self.recording().transcript,
                        self.conversation,
                        self.recording
                    )
                );
            }

            self.hasScreenRecording(
                ko.unwrap(self.recording().media) === "screen"
            );
            if (!self.hasEmail() && self.hasScreenRecording()) {
                self.screenRecordingDetailsView(
                    self.subView(ScreenRecordingDetailsView, options.url)
                );
                self.screenRecordingDetailsView().header.setHeader(
                    self.recording().startTime,
                    self.recording().endTime
                );
            }

            if (self.isArchived) {
                self.hasAudio() ||
                self.hasScreenRecording() ||
                !self.conversation() ||
                (!self.conversation().startTime && !self.conversation().endTime)
                    ? self
                          .header()
                          .setStartEndTime(
                              self.recording().startTime,
                              self.recording().endTime
                          )
                    : self
                          .header()
                          .setStartEndTime(
                              self.conversation().startTime
                                  ? self.conversation().startTime()
                                  : null,
                              self.conversation().endTime
                                  ? self.conversation().endTime()
                                  : null
                          );
            }
        }
    });

    self.pause = function () {
        if (self.audioPlayerView.isPlaying()) {
            self.audioPlayerView.pause();
        }
    };

    self.play = function () {
        if (self.audioPlayerView.isPlaying()) {
            self.audioPlayerView.play();
        }
    };

    self.saveAnnotation = function () {
        const annotationText = self.audioPlayerView.annotationTextInput();
        if (annotationText.length > 0) {
            if (!self.audioPlayerView.annotationTextInput.isValid()) {
                if (isSparkFlareFTEnabled) {
                    return;
                }
                toasts.notifyMajorAlert(
                    "playback.recordingAnnotation.descriptionTooLong.title",
                    {
                        bodyMessageKey:
                            "playback.recordingAnnotation.descriptionTooLong.body",
                        messageData: {
                            maxChar: maxDescriptionChar,
                        },
                    }
                );
                return Bluebird.reject(
                    errorCallbackFactory.createCallback({
                        namespace:
                            "playback.recordingAnnotation.descriptionTooLong",
                    })
                );
            }
            /*
             * ininAudioPlayer does not rewind (currently) once the playback finishes so if you
             * create an annotation after the initial playback and use getPosition() of the player,
             * your annotation will appear at the end, instead of the start
             */
            const position =
                (self.audioPlayerView.percentPosition() *
                    self.audioPlayerView.getDuration()) /
                100;
            return qualityManagementService
                .createAnnotation(self.recording(), annotationText, position)
                .then(function (annotation) {
                    annotation.animatePlacement = true;
                    self.audioPlayerView.annotationsList.push(annotation);
                    self.audioPlayerView.annotationTextInput("");
                })
                .catch(
                    errorCallbackFactory.createCallback({
                        namespace: "playback.recordingAnnotation",
                    })
                );
        }
        return Bluebird.reject(
            errorCallbackFactory.createCallback({
                namespace: "playback.recordingAnnotation.missingTextInput",
            })
        );
    };
});

export default RecordingView;
